import { ImportExportMetadata, ImportJob } from "../models/import";
import { ProjectId, Url } from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";

export const getAssetImportClient = (
  http: AssetsHttpClient,
  projectId: ProjectId,
  // Uploads target a self-signed S3/OSS URL — any Authorization/region header
  // added by the injected AssetsHttpClient would invalidate the signature, so
  // uploads bypass it entirely via a plain fetch (injectable for tests).
  rawFetch: typeof fetch = fetch
) => {
  const base = http.getAssetsServiceBasePath(projectId);

  return {
    get: (jobId: ImportJob["id"]): Promise<ImportJob> =>
      http.get<ImportJob>(`${base}/${Endpoints.AssetImports}/${jobId}`),

    create: (
      includedModules?: ImportExportMetadata["includedModules"]
    ): Promise<ImportJob> =>
      http.post<ImportJob>(`${base}/${Endpoints.AssetImports}`, {
        body: JSON.stringify({
          options: { createProgressTrackingChannel: true },
          includedModules,
        }),
      }),

    upload: async (url: Url, fileData: BodyInit): Promise<Response> => {
      const response = await rawFetch(url, {
        method: "PUT",
        body: fileData,
        headers: {
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "Content-Disposition": "attachment",
        },
      });
      if (!response.ok) {
        throw new Error(
          `Asset import upload failed: ${response.status} ${response.statusText}`
        );
      }
      return response;
    },

    complete: (jobId: ImportJob["id"]): Promise<ImportJob> =>
      http.put<ImportJob>(
        `${base}/${Endpoints.AssetImports}/${jobId}/status/uploaded`
      ),
  };
};

/**
 * The shape returned by {@link getAssetImportClient}.
 * Exported so consumers can type variables without spelling out
 * `ReturnType<typeof getAssetImportClient>`.
 */
export type AssetImportClient = ReturnType<typeof getAssetImportClient>;
